import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {User, USER_SHAPE} from 'constants/UserConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TeamMembersQuery, TeamMembersQueryVariables} from 'metadata/__generated/TeamMembersQuery';
import {Party} from 'parties/PartyConstants';
import {WHITE} from 'ui-library/Colors';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import APIUtil from 'util/APIUtil';
import * as modals from 'util/Modals';

import FieldActionButtons from './FieldActionButtons';

interface Props {
  authors?: Array<User> | null;
  fileId: string;
  isReadOnly: boolean;
  isRegistered: boolean;
  currentUser: EntityStoreUser;
  // NOTE: `shouldAllowTeams` does not add the team to the authors list, only the members of the team.
  shouldAllowTeams: boolean;
}

interface State {
  isBeingEdited: boolean;
  newAuthors: Array<Party>;
}

export const TEAM_MEMBERS_QUERY = gql`
  query TeamMembersQuery($id: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    team(id: $id) {
      ... on TeamNode {
        id
        members {
          id
          email
          handle
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

class AuthorList extends React.Component<Props, State> {
  static propTypes = {
    authors: PropTypes.arrayOf(USER_SHAPE.isRequired).isRequired,
    fileId: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
    isRegistered: PropTypes.bool.isRequired,
    currentUser: PropTypes.shape({
      id: PropTypes.string.isRequired,
      organizations: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string.isRequired}).isRequired)
        .isRequired,
    }),
    shouldAllowTeams: PropTypes.bool.isRequired,
  };

  override state: State = {
    isBeingEdited: false,
    newAuthors: [],
  };

  _handleEdit = (): void => {
    this.setState({
      isBeingEdited: true,
      newAuthors: this.props.authors ?? [],
    });
  };

  _handleCancel = (): void => {
    this.setState({isBeingEdited: false});
  };

  _isEntry = (): boolean => {
    const entryTypes = [ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY];
    return entryTypes.includes(APIUtil.apiIdToType(this.props.fileId));
  };

  _isAuthor(authorList: Array<EntityStoreUser>): boolean {
    return Boolean(this.props.currentUser) && authorList.some(({id}) => id === this.props.currentUser.id);
  }

  _handleComplete = async (): Promise<void> => {
    const authorIds = this.state.newAuthors.map(({id}) => id);
    const {warnings} = await FileBrowserDAO.validateAuthors(this.props.fileId, authorIds);
    if (warnings.length) {
      try {
        await modals.confirm(
          'Updating Authors',
          <div>
            Are you sure you want to remove yourself as an author?{' '}
            {warnings.map((warning: {message: string}) => warning.message).join(' ')}
          </div>
        );
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (resp) {
        this._handleCancel();
        return;
      }
    }
    await FileBrowserActions.updateAuthorsWithDataMods(this.props.fileId, authorIds, this.props.isRegistered);
    this.setState({isBeingEdited: false});
  };

  _getAuthorTags(): Array<{id: string; handle: string; text: string}> {
    return this.state.newAuthors.map(({handle, id}) => ({id, handle, text: handle}));
  }

  _updateAuthors = async (newAuthors: Array<Party>): Promise<void> => {
    const selectedTeam = newAuthors.find(({type}) => type === ModelTypeConstants.TEAM);
    if (selectedTeam) {
      const {team} = await doQuerySafe<TeamMembersQuery, TeamMembersQueryVariables>(
        {query: TEAM_MEMBERS_QUERY},
        {variables: {id: selectedTeam.id}}
      );
      assert(!isInaccessible(team), 'Expected team to be accessible.');
      // Filter the team out of the authors list and dedupe to ensure existing authors on the team
      // are not duplicated
      const authorsToSet = _.unique(
        [...newAuthors.filter(({type}) => type !== ModelTypeConstants.TEAM), ...team.members],
        'id'
      );
      this.setState({newAuthors: authorsToSet as Array<Party>});
    } else {
      this.setState({newAuthors});
    }
  };

  override render(): JSX.Element {
    return (
      <div
        className={classNames('metadata-field metadata-field--authorList', {
          [css(styles.hover)]: this.props.isReadOnly,
        })}
      >
        <div className="metadata-field-row">
          <div className="metadata-field-col">
            {(() => {
              if (this.state.isBeingEdited) {
                // currentUser should be defined since the file is writable.
                return (
                  <EntityInputWithSearch
                    allowAdjustInputWidth={false}
                    allowTeams={this.props.shouldAllowTeams}
                    allowOrganizations={false}
                    allowBenchlingApps={true}
                    onChange={this._updateAuthors}
                    organizationIds={this.props.currentUser.organizations.map(({id}) => id)}
                    size={TagsInputSize.SMALL}
                    value={this._getAuthorTags()}
                  />
                );
              } else if (this.props.authors && this.props.authors.length > 0) {
                return this.props.authors.map((user) => (
                  <div key={user.handle}>
                    <a
                      key={user.handle}
                      href={`/${user.url_identifier}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {user.name}
                    </a>
                  </div>
                ));
              } else {
                return 'This file has no authors.';
              }
            })()}
          </div>
          <div className="registry-actionGroupContainer">
            {!this.props.isReadOnly && (
              <FieldActionButtons
                isEditing={this.state.isBeingEdited}
                cancel={this._handleCancel}
                complete={this._handleComplete}
                isCompleteEnabled={true}
                edit={this._handleEdit}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  hover: {
    ':hover': {
      backgroundColor: WHITE,
    },
  },
});

export default AuthorList;
